import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

/**
 * When the local user is the only one left in a call, we do NOT drop them —
 * instead, after `timeoutMs` alone we surface a prompt ("you're the only one —
 * end the call?"). Snooze re-arms the timer; the prompt auto-dismisses and the
 * timer clears the moment someone else (re)joins. Purely client-side (a
 * `setTimeout`) — no server scheduler or hardware work required.
 *
 * @param isAlone   true when the local user is the sole participant
 * @param timeoutMs how long to wait alone before prompting (default 5 min)
 */
export function useLoneParticipantPrompt(
  isAlone: boolean,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): { promptOpen: boolean; snooze: () => void } {
  const [promptOpen, setPromptOpen] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const arm = useCallback(() => {
    clearTimer();
    timerRef.current = setTimeout(() => setPromptOpen(true), timeoutMs);
  }, [clearTimer, timeoutMs]);

  useEffect(() => {
    if (!isAlone) {
      // Someone else is here — cancel the countdown and dismiss any open prompt.
      clearTimer();
      setPromptOpen(false);
      return;
    }
    arm();
    return clearTimer;
  }, [isAlone, arm, clearTimer]);

  const snooze = useCallback(() => {
    setPromptOpen(false);
    arm(); // wait another full interval
  }, [arm]);

  return { promptOpen, snooze };
}
